import { useEffect, useRef, useState } from 'react';

import { api } from '@/lib/api';

interface ClaimMessage {
  sessionId: string;
  ts: number;
}

interface BackendResource {
  resourceType: 'experience' | 'task';
  resourceId: string;
}

const HEARTBEAT_INTERVAL_MS = 6000;

/** Take-over: the newest tab/device to open a given resource (a task or an experience) holds
 * write access; every other tab/device already showing that same resource flips to read-only.
 *
 * Two independent layers, both driving the same `readOnly` flag:
 * - **Same-browser-tab** (BroadcastChannel, always on): sub-second, no backend involved.
 * - **Cross-device** (`backend` param, opt-in): claims ownership via `POST /sessions/claim` on
 *   mount, then polls `POST /sessions/heartbeat` every ~6s for as long as this tab is the owner.
 *   A heartbeat response naming a different `sessionId` means some other device claimed it —
 *   flips read-only exactly like a same-tab claim would. Stops polling once read-only (no
 *   auto-reclaim; matches the same-tab layer's own "user must explicitly click Resume" model).
 *
 * `resourceKey` must already be namespaced by resource type (e.g. `task-${taskId}`,
 * `experience-${experienceId}`) so a task and an experience never collide on one BroadcastChannel. */
export function useTaskTakeOver(resourceKey: string | undefined, backend?: BackendResource) {
  const [readOnly, setReadOnly] = useState(false);
  const sessionIdRef = useRef('');
  const claimedAtRef = useRef(0);
  const readOnlyRef = useRef(false);
  // The resource the current session id belongs to. Used to mint a session id exactly ONCE per
  // resource — see the guard below.
  const sessionKeyRef = useRef<string | undefined>(undefined);

  function flipReadOnly() {
    readOnlyRef.current = true;
    setReadOnly(true);
  }

  useEffect(() => {
    readOnlyRef.current = false;
    setReadOnly(false);
    if (!resourceKey) return;
    // Mint a session id (and claim timestamp) ONCE per resource, not on every effect run. React
    // StrictMode double-invokes effects on mount in dev; regenerating here would mint a second id
    // and fire a second backend `claim` that races the first. Since `claim` is a last-write-wins
    // upsert, if the older claim's write lands last the heartbeat then sees a "different" current
    // session and wrongly flips read-only — a phantom take-over on a task that isn't open anywhere
    // else. Reusing the same id makes the replayed claim idempotent. A genuine resource change (or
    // a full remount after resume()'s reload) still mints a fresh id, so newest-tab-wins is intact.
    if (sessionKeyRef.current !== resourceKey) {
      sessionKeyRef.current = resourceKey;
      sessionIdRef.current = crypto.randomUUID();
      claimedAtRef.current = Date.now();
    }

    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(`agavi-session-${resourceKey}`);
    channel.postMessage({ sessionId: sessionIdRef.current, ts: claimedAtRef.current } satisfies ClaimMessage);

    function onMessage(e: MessageEvent<ClaimMessage>) {
      const { sessionId, ts } = e.data;
      if (sessionId === sessionIdRef.current) return;
      // A newer claim from a different tab always wins; a tie (same millisecond) breaks on
      // sessionId so every tab agrees on the same winner without a coordinator.
      if (ts > claimedAtRef.current || (ts === claimedAtRef.current && sessionId > sessionIdRef.current)) {
        flipReadOnly();
      }
    }
    channel.addEventListener('message', onMessage);

    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [resourceKey]);

  useEffect(() => {
    if (!backend) return;
    const { resourceType, resourceId } = backend;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    /*
     * ANSWER THE QUESTION THAT WAS ASKED.
     *
     * `sessionIdRef` is re-minted whenever the resource changes, and a heartbeat is a round
     * trip -- so a teacher clicking through tasks faster than ~6s left this comparing the id
     * that claimed the task they have LEFT against the id minted for the one they are now on.
     * Those never match, so it flipped read-only and told them the task was open in another
     * tab, which it was not. Reported as exactly that: clicking through tasks quickly loses a
     * preview session that was being used to test recall across them.
     *
     * So the id is captured before the await and every decision is made against that captured
     * value. That alone fixes the lock-out, and it is the half the tests pin.
     *
     * The `cancelled` half beside it covers a case they do not reach: an unmount leaves the
     * closure holding a ref whose `.current` never moves again, so the captured id still
     * matches and only `cancelled` says the effect is gone. It costs a comparison and stops a
     * state update on a dead component.
     */
    async function poll() {
      if (cancelled || readOnlyRef.current) return;
      const asked = sessionIdRef.current;
      try {
        const res = await api.post<{ current: { sessionId: string } | null }>('/sessions/heartbeat', {
          resourceType,
          resourceId,
          sessionId: asked,
        });
        if (cancelled || asked !== sessionIdRef.current) return;
        if (res.current && res.current.sessionId !== asked) {
          flipReadOnly();
          return; // superseded — stop polling, same "no auto-reclaim" rule as the same-tab layer
        }
      } catch {
        // Transient network hiccup — try again next tick rather than flipping read-only on it;
        // a real supersession will show up on a later heartbeat regardless.
      }
      if (!cancelled && !readOnlyRef.current) timer = setTimeout(() => void poll(), HEARTBEAT_INTERVAL_MS);
    }

    async function claimThenPoll() {
      const asked = sessionIdRef.current;
      try {
        await api.post('/sessions/claim', {
          resourceType,
          resourceId,
          sessionId: asked,
        });
      } catch {
        // A failed claim just means the cross-device signal won't arrive this round-trip — the
        // same-tab BroadcastChannel layer keeps working independently of this.
      }
      // Same rule: a claim that lands after the teacher has moved on belongs to a resource this
      // effect is no longer watching, and must not start a poll loop for it.
      if (cancelled || asked !== sessionIdRef.current) return;
      void poll();
    }

    void claimThenPoll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backend?.resourceType, backend?.resourceId]);

  /** The other tab/device's edits are already persisted (autosave) by the time this fires, so a
   * full reload is the simplest correct way to pick up whatever it last saved. The remount this
   * triggers re-posts a same-tab claim AND re-claims the backend session with a newer timestamp —
   * that's what flips the other side read-only, no separate "reclaim" call needed here. */
  function resume() {
    window.location.reload();
  }

  return { readOnly, resume };
}
